if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        if (!this.elements.viewer || !this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button) return;

          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        this.initializeCompetitionIntroMedia();
      }

      isCompetitionJavelinPage() {
        const productInfo = this.closest('product-info');
        const productUrl = productInfo?.dataset?.url || window.location.pathname || '';

        return productUrl.includes('/products/competition-javelin');
      }

      getViewerList() {
        return this.elements.viewer?.querySelector('[id^="Slider-Gallery"]') || this.elements.viewer?.querySelector('ul');
      }

      getIntroMediaSlide() {
        const viewerList = this.getViewerList();
        if (!viewerList) return null;

        return viewerList.querySelector('[data-mak-intro-media="true"]');
      }

      getThumbnailForMedia(mediaId) {
        if (!this.elements.thumbnails || !mediaId) return null;

        return this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
      }

      initializeCompetitionIntroMedia() {
        if (!this.isCompetitionJavelinPage()) return;
        if (this.dataset.makIntroInitialized === 'true') return;

        const viewerList = this.getViewerList();
        if (!viewerList) return;

        const mediaSlides = Array.from(viewerList.querySelectorAll('[data-media-id]'));
        const introMedia = mediaSlides.find((slide) => slide.querySelector('video'));

        if (!introMedia) return;

        const introMediaId = introMedia.dataset.mediaId;
        const introThumbnail = this.getThumbnailForMedia(introMediaId);

        this.dataset.makIntroInitialized = 'true';

        introMedia.dataset.makIntroMedia = 'true';
        introMedia.classList.add('mak-intro-media-slide');

        mediaSlides.forEach((slide) => slide.classList.remove('is-active'));
        introMedia.classList.add('is-active');

        if (viewerList.firstElementChild !== introMedia) {
          viewerList.prepend(introMedia);
        }

        if (introThumbnail && introThumbnail.parentElement.firstElementChild !== introThumbnail) {
          introThumbnail.parentElement.prepend(introThumbnail);
        }

        if (introThumbnail) {
          this.setActiveThumbnail(introThumbnail);
        }

        this.prepareIntroVideo(introMedia);

        window.setTimeout(() => {
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();

          const slider = this.getViewerList();
          if (slider) slider.scrollTo({ left: 0, behavior: 'auto' });
        }, 0);
      }

      prepareIntroVideo(introMedia) {
        if (!introMedia) return;

        const container = introMedia.querySelector('[data-mak-direct-video]');
        const video = introMedia.querySelector('video');
        const poster = introMedia.querySelector('.mak-direct-video-poster');
        const playButton = introMedia.querySelector('[data-mak-video-play], .mak-direct-video-play');

        if (!video) return;

        if (container) {
          container.classList.add('is-playing', 'mak-intro-moving-image');
        }

        if (poster) {
          poster.style.display = 'none';
        }

        if (playButton) {
          playButton.style.display = 'none';
        }

        video.muted = true;
        video.loop = true;
        video.controls = false;
        video.autoplay = true;
        video.playsInline = true;
        video.preload = 'auto';

        video.removeAttribute('controls');
        video.setAttribute('muted', '');
        video.setAttribute('loop', '');
        video.setAttribute('autoplay', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');

        video.style.pointerEvents = 'none';

        const playPromise = video.play();

        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );

        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) {
          return;
        }

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });

        activeMedia?.classList?.add('is-active');

        if (prepend) {
          const introMedia = this.getIntroMediaSlide();

          /*
            Keep the animated intro/video as the first gallery media on the Competition Javelin PDP.
            Variant changes can still activate the correct variant image, but they should not move
            the variant image above the intro animation.
          */
          if (!introMedia || activeMedia === introMedia) {
            activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

            if (this.elements.thumbnails) {
              const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);

              if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
                activeThumbnail.parentElement.prepend(activeThumbnail);
              }
            }

            if (this.elements.viewer.slider) this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }

          const activeMediaRect = activeMedia.getBoundingClientRect();

          if (activeMediaRect.top > -0.5) return;

          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);

        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail?.dataset?.mediaPosition) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));

        const thumbnailButton = thumbnail.querySelector('button');
        if (thumbnailButton) thumbnailButton.setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');

        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);

          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        if (activeItem?.dataset?.makIntroMedia === 'true') {
          this.prepareIntroVideo(activeItem);
          return;
        }

        window.pauseAllMedia();

        const deferredMedia = activeItem.querySelector('.deferred-media');

        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}